import { createElement, ReactNode } from 'react';

type TextFormattingTag = 'b' | 'i' | 'u';

const FORMATTING_TAG = /\[\/?[biu]\]/gi;

const ELEMENT_BY_TAG: Record<TextFormattingTag, string> = { b: 'strong', i: 'em', u: 'u' };

type Tag = {
  name: TextFormattingTag;
  closing: boolean;
  start: number;
  end: number;
  partner: Tag | null;
  crossed: boolean;
};

const parseTags = (text: string): Tag[] => {
  const tags = Array.from(text.matchAll(FORMATTING_TAG), (match): Tag => {
    const token = match[0];
    return {
      name: token[token.length - 2].toLowerCase() as TextFormattingTag,
      closing: token[1] === '/',
      start: match.index ?? 0,
      end: (match.index ?? 0) + token.length,
      partner: null,
      crossed: false,
    };
  });

  const openByName: Record<TextFormattingTag, Tag[]> = { b: [], i: [], u: [] };
  for (const tag of tags) {
    if (!tag.closing) {
      openByName[tag.name].push(tag);
      continue;
    }
    const opening = openByName[tag.name].pop();
    if (opening) {
      opening.partner = tag;
      tag.partner = opening;
    }
  }

  const open: Tag[] = [];
  for (const tag of tags) {
    if (!tag.partner) continue;
    if (!tag.closing) {
      open.push(tag);
      continue;
    }
    const index = open.lastIndexOf(tag.partner);
    const crossing = open.splice(index).slice(1);
    open.push(...crossing);
    for (const other of crossing) {
      other.crossed = true;
      tag.crossed = true;
      tag.partner.crossed = true;
    }
  }

  return tags;
};

const getMatchedTags = (text: string): Tag[] =>
  parseTags(text).filter((tag) => tag.partner && !tag.crossed && !tag.partner.crossed);

export function formatTextToReact<T extends null | undefined = never>(
  text: string | T
): ReactNode | T {
  if (!text) return text;

  type Frame = { children: ReactNode[] };
  const stack: Frame[] = [{ children: [] }];
  let position = 0;
  let key = 0;

  for (const tag of getMatchedTags(text)) {
    const current = stack[stack.length - 1];
    if (tag.start > position) {
      current.children.push(text.slice(position, tag.start));
    }
    position = tag.end;

    if (!tag.closing) {
      stack.push({ children: [] });
      continue;
    }
    const frame = stack.pop() as Frame;
    stack[stack.length - 1].children.push(
      createElement(ELEMENT_BY_TAG[tag.name], { key: key++ }, ...frame.children)
    );
  }

  const root = stack[0];
  if (position < text.length) {
    root.children.push(text.slice(position));
  }
  return root.children.length === 1 ? root.children[0] : root.children;
}
